import { useContext } from "react";

import moment, { unitOfTime } from "moment-timezone";

import LanguageContext from "../../context/language/LanguageContext";
import { TimeZoneContext } from "../../context/TimeZone/TimeZoneProvider";
import { DATE_FORMATS, DateType, LANGUAGES, TIME_VALUES } from "../types";

export const useDateTime = () => {
  const { currentDateFormat, userBrowserDateFormat, language } =
    useContext(LanguageContext);

  const { systemTimeZone, currentTimeZone, revertToSystemTimeZone } =
    useContext(TimeZoneContext);

  const currentTimeFormatHoursMinutes = `${currentDateFormat} ${DATE_FORMATS.HoursMinutes}`;
  const defaultDetailsTimeStampFormat = `${currentDateFormat} ${DATE_FORMATS.HoursMinutesSeconds}`;
  const currentDateFormatWithoutTime = `${currentDateFormat}`;
  const switchedDashToSlashDateFormatWithoutTime = currentDateFormatWithoutTime
    .split("-")
    .join("/");

  const checkIfDateIsNull = (dateInput: DateType) =>
    dateInput === null || dateInput === "-";

  const formattedDate = (
    date: DateType,
    format: string = defaultDetailsTimeStampFormat,
    addEndOfTheDayTime?: boolean
  ) => {
    if (date !== "undefined") {
      if (checkIfDateIsNull(date)) {
        return "-";
      }

      if (addEndOfTheDayTime) {
        return moment
          .utc(date)
          .tz(currentTimeZone)
          .startOf("day")
          .add(23, "hours")
          .add(59, "minutes")
          .format(format);
      }

      return moment.utc(date).tz(currentTimeZone).format(format);
    }
  };

  const formattedDateWithoutLocalTimeZone = (
    timeString: string,
    format: string
  ) => moment.utc(timeString).format(format);

  const formatDateToEnString = (date?: string, short?: boolean) => {
    return (
      date &&
      moment(date)
        .locale(language ?? LANGUAGES.English)
        .format(
          short ? DATE_FORMATS.MonthDayYearShort : DATE_FORMATS.EnUsString
        )
    );
  };

  const formatDateIntoMonthYearString = (date?: string) =>
    moment(date).locale(language).format(DATE_FORMATS.MonthYear);

  const startOfTime = (period: string) =>
    moment().startOf(period as unitOfTime.StartOf);

  const isValidDateFormat = (input: string, format: DATE_FORMATS) =>
    moment(input, format, true).isValid();

  const convertDateToUTC = (input: string) => {
    let parsedDate = moment(input, moment.ISO_8601, true);

    if (!parsedDate.isValid()) {
      const [datePart, timePart] = input.split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes] = (timePart || TIME_VALUES.Midnight)
        .split(":")
        .map(Number);

      parsedDate = moment({
        year: Number.isNaN(year) ? 1970 : year,
        month: Number.isNaN(month) ? 0 : month - 1,
        day: Number.isNaN(day) ? 1 : day,
        hour: Number.isNaN(hours) ? 0 : Math.min(hours, 23),
        minute: Number.isNaN(minutes) ? 0 : Math.min(minutes, 59)
      });
    }

    return parsedDate.utc().format();
  };

  const convertToCurrentTimezone = (input: string | null) =>
    moment.utc(input, DATE_FORMATS.ISO_8601).tz(currentTimeZone);

  const subtractDaysFromDate = (
    days: number,
    date: DateType,
    format?: string
  ) => {
    const dateObj = moment.utc(date).tz(currentTimeZone).subtract(days, "days");

    return format ? dateObj.format(format) : dateObj;
  };

  const getUTCDate = (date?: string | null) => moment.utc(date);

  const convertToCurrentDateFormat = (dateInput: DateType) => {
    if (checkIfDateIsNull(dateInput)) {
      return "-";
    }

    return moment(dateInput).format(currentDateFormat);
  };

  const currentYear = moment().year();
  const currentMonth = moment().month() + 1;

  return {
    currentTimeZone,
    currentDateFormat,
    userBrowserDateFormat,
    currentTimeFormatHoursMinutes,
    defaultDetailsTimeStampFormat,
    systemTimeZone,
    convertDateToUTC,
    getUTCDate,
    convertToCurrentTimezone,
    convertToCurrentDateFormat,
    formattedDate,
    startOfTime,
    isValidDateFormat,
    revertToSystemTimeZone,
    subtractDaysFromDate,
    formatDateToEnString,
    currentDateFormatWithoutTime,
    switchedDashToSlashDateFormatWithoutTime,
    formattedDateWithoutLocalTimeZone,
    formatDateIntoMonthYearString,
    checkIfDateIsNull,
    currentYear,
    currentMonth
  };
};
